import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTruckPickup, faTruckFast, faSpinner } from "@fortawesome/free-solid-svg-icons";
import AddressAutocomplete, { AddressComponents } from '../common/AddressAutocomplete';
import { utilsService } from '../../services/utils.service';
import { countryService, CountryFull } from '../../services/country.service';
import { authService } from '../../services/auth.service';
import useForm from '../../hooks/useForm';
import { formatPhoneNational, isValidPhone, getPhonePlaceholder } from '../../config/phone.utils';
import { normalizeZip, isValidZipFormat, getZipFormatErrorMessage, getZipPlaceholder } from '../../config/zip.utils';
import '../../styles/app/PickupForm.css';
import elPaketLogoShortIMG from "../../assets/images/logo-elpaket-short.png";

const GOOGLE_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || "";

const getTomorrowDate = () => {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    const day = String(tomorrow.getDate()).padStart(2, '0');
    const month = String(tomorrow.getMonth() + 1).padStart(2, '0');
    const year = tomorrow.getFullYear();
    return `${year}-${month}-${day}`;
};

const getMaxDate = () => {
    const maxDate = new Date();
    maxDate.setDate(maxDate.getDate() + 7);
    const day = String(maxDate.getDate()).padStart(2, '0');
    const month = String(maxDate.getMonth() + 1).padStart(2, '0');
    const year = maxDate.getFullYear();
    return `${year}-${month}-${day}`;
};


interface PickupFormProps {
    isModal?: boolean;
}

const PickupForm: React.FC<PickupFormProps> = ({ isModal = false }) => {
    const navigate = useNavigate();
    const [zipError, setZipError] = useState<string | null>(null);
    const [isZipValid, setIsZipValid] = useState(false);
    const [countryData, setCountryData] = useState<CountryFull | null>(null);
    const [isValidatingEmail, setIsValidatingEmail] = useState(false);
    const [emailExists, setEmailExists] = useState<boolean | null>(null);

    const initialValues = {
        firstName: '',
        lastName: '',
        phone: '',
        email: '',
        pickupType: 'oficina',
        address: '',
        city: '',
        state: '',
        zipCode: '',
        date: getTomorrowDate(),
        notes: ''
    };

    const validationRules = {
        firstName: (value: string) =>
            !value
                ? "El nombre es requerido"
                : !/^[a-zA-ZáéíóúÁÉÍÓÚñÑ\s]+$/.test(value)
                    ? "El nombre no debe contener números ni caracteres especiales"
                    : null,
        lastName: (value: string) =>
            !value
                ? "El apellido es requerido"
                : !/^[a-zA-ZáéíóúÁÉÍÓÚñÑ\s]+$/.test(value)
                    ? "El apellido no debe contener números ni caracteres especiales"
                    : null,
        email: (value: string) =>
            !value
                ? "El email es requerido"
                : !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)
                    ? "El formato del email no es válido"
                    : null,
        phone: (value: string) => {
            if (!value) return "El teléfono es requerido";
            if (!isValidPhone(value, 'US')) return "Número de teléfono inválido";
            return null;
        },
        address: (value: string) => (!value ? "La dirección es requerida" : null),
        city: (value: string) => (!value ? "La ciudad es requerida" : null),
        state: (value: string) => (!value ? "El estado es requerido" : null),
        zipCode: (value: string) => {
            if (!value) return "El código postal es requerido";
            if (!isValidZipFormat(value, 'US')) return getZipFormatErrorMessage('US');
            return null;
        },
        date: (value: string) => {
            if (!value) return "La fecha es requerida";
            const selectedDate = new Date(value + 'T00:00:00');
            const today = new Date();
            today.setHours(0,0,0,0);
            const tomorrow = new Date(today);
            tomorrow.setDate(tomorrow.getDate() + 1);
            const maxDate = new Date(today);
            maxDate.setDate(maxDate.getDate() + 7);
            
            const selTime = selectedDate.getTime();
            if (selTime < tomorrow.getTime()) {
                return "La fecha debe ser a partir de mañana";
            }
            if (selTime > maxDate.getTime()) {
                return "La fecha no puede ser mayor a 7 días en el futuro";
            }
            return null;
        },
        notes: (value: string) => (!value ? "La descripción del contenido es requerida" : null),
    };

    const { values, errors, handleChange, setValues, setErrors, validate } = useForm(
        initialValues,
        validationRules
    );

    useEffect(() => {
        const fetchCountry = async () => {
            try {
                const data = await countryService.getCountry('US');
                setCountryData(data);
            } catch (error) {
                console.error("Error fetching US country data:", error);
            }
        };
        fetchCountry();
    }, []);

    const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const val = e.target.value;
        const formatted = formatPhoneNational(val, 'US', countryData?.phoneCode || '+1');
        setValues(prev => ({ ...prev, phone: formatted }));

        const isVal = isValidPhone(formatted, 'US');
        setErrors((prev: any) => ({
            ...prev,
            phone: !formatted ? "El teléfono es requerido" : !isVal ? "Número de teléfono inválido" : null,
        }));
    };

    const validateEmailExistence = async (email: string) => {
        if (!email) {
            setEmailExists(null);
            return;
        }
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
            return;
        }
        setIsValidatingEmail(true);
        try {
            const result = await authService.checkCustomerExist(email);
            setEmailExists(result.exist);
        } catch (error) {
            console.error("Error checking email existence:", error);
            setEmailExists(null);
        } finally {
            setIsValidatingEmail(false);
        }
    };

    const handleAddressSelect = (addressData: AddressComponents) => {
        const normalizedZip = normalizeZip(addressData.zipCode || '', 'US');
        setValues(prev => ({
            ...prev,
            address: addressData.fullAddress,
            city: addressData.city || prev.city,
            state: addressData.state || prev.state,
            zipCode: normalizedZip || prev.zipCode,
        }));
        setErrors(prev => ({
            ...prev,
            address: undefined,
            city: undefined,
            state: undefined,
            zipCode: undefined
        }));
        setZipError(null);
        setIsZipValid(true);
    };

    const validateZipCode = async (zipToValidate?: string) => {
        const rawZip = zipToValidate !== undefined ? zipToValidate : values.zipCode;
        const zip = normalizeZip(rawZip || '', 'US');
        if (!zip) {
            setZipError("El código postal es requerido");
            setIsZipValid(false);
            return false;
        }

        if (!isValidZipFormat(zip, 'US')) {
            setZipError(getZipFormatErrorMessage('US'));
            setIsZipValid(false);
            return false;
        }

        // Format is valid for US (5 digits)
        setZipError(null);
        setIsZipValid(true);

        // Optionally enrich city / state if not already set
        try {
            const location = await utilsService.getLocationByZipcode({
                iso2code: 'US',
                zipcode: zip,
            });

            if (location) {
                setValues(prev => {
                    const newCity = prev.city || location.city;
                    const newState = prev.state || location.state;
                    setErrors((prevErrors: any) => ({
                        ...prevErrors,
                        city: newCity ? undefined : prevErrors.city,
                        state: newState ? undefined : prevErrors.state,
                    }));
                    return {
                        ...prev,
                        city: newCity,
                        state: newState,
                    };
                });
            }
        } catch (error) {
            console.warn('Non-blocking zip lookup notice:', error);
        }

        return true;
    };

    const handleZipBlur = () => {
        if (values.zipCode) {
            validateZipCode();
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        
        const isFormValid = validate();
        if (!isFormValid) return;

        if (!values.zipCode || !isValidZipFormat(values.zipCode, 'US')) {
            setZipError(getZipFormatErrorMessage('US'));
            return;
        }

        navigate('/pickup', {
            state: {
                ...values,
                fullName: `${values.firstName} ${values.lastName}`.trim(),
                emailExists,
                packageContent: values.notes,
                fromLanding: true
            }
        });
    };

    return (
        <div className={isModal ? "" : "pickup-form-card"} style={isModal ? { border: "none", padding: 0, background: "transparent", boxShadow: "none" } : {}}>
            {!isModal && (
                <h3 className="pickup-form-title">
                    <img 
                        src={elPaketLogoShortIMG} 
                        alt="elPaket" 
                        style={{ height: '26px', objectFit: 'contain' }}
                    /> Solicita tu Recogida Ahora
                </h3>
            )}
            <form onSubmit={handleSubmit} className="pickup-form-content">
                <div className="pickup-form-row">
                    <div className="pickup-field" style={{ flex: 1 }}>
                        <label>Nombre *</label>
                        <input type="text" name="firstName" placeholder="Juan" value={values.firstName} onChange={handleChange} maxLength={50} required className={errors.firstName ? "is-invalid" : ""} />
                        {errors.firstName && <span className="pick-up-form-error-message">{errors.firstName}</span>}
                    </div>
                    <div className="pickup-field" style={{ flex: 1 }}>
                        <label>Apellido *</label>
                        <input type="text" name="lastName" placeholder="Pérez" value={values.lastName} onChange={handleChange} maxLength={50} required className={errors.lastName ? "is-invalid" : ""} />
                        {errors.lastName && <span className="pick-up-form-error-message">{errors.lastName}</span>}
                    </div>
                </div>

                <div className="pickup-form-row">
                    <div className="pickup-field" style={{ flex: 1 }}>
                        <label>Teléfono *</label>
                        <div className="phone-input-wrapper">
                            {countryData && (
                                <div className="phone-prefix-addon">
                                    <img
                                        src={`https://flagcdn.com/w40/${countryData.iso2Country.toLowerCase()}.png`}
                                        alt={countryData.name}
                                        className="phone-flag-img"
                                    />
                                    <span className="phone-code-text">+{countryData.phoneCode.replace("+", "")}</span>
                                </div>
                            )}
                            <input type="tel" name="phone" placeholder={getPhonePlaceholder(countryData?.iso2Country || "US")} value={values.phone} onChange={handlePhoneChange} className={`phone-input-field ${errors.phone ? "is-invalid" : ""}`} maxLength={15} required />
                        </div>
                        {errors.phone && <span className="pick-up-form-error-message">{errors.phone}</span>}
                    </div>
                    <div className="pickup-field" style={{ flex: 1 }}>
                        <label>Email *</label>
                        <div style={{ position: 'relative' }}>
                            <input 
                                type="email" 
                                name="email" 
                                placeholder="juan@email.com" 
                                value={values.email} 
                                onChange={handleChange} 
                                onBlur={() => validateEmailExistence(values.email)}
                                maxLength={100} 
                                required 
                                className={errors.email ? "is-invalid" : ""}
                                style={{ paddingRight: isValidatingEmail ? '35px' : '10px' }}
                            />
                            {isValidatingEmail && (
                                <div className="email-inline-loader" style={{ position: 'absolute', right: '12px', top: '50%', transform: 'translateY(-50%)', color: '#666' }}>
                                    <FontAwesomeIcon icon={faSpinner} spin />
                                </div>
                            )}
                        </div>
                        {errors.email && <span className="pick-up-form-error-message">{errors.email}</span>}
                    </div>
                </div>

                <div className="pickup-form-row">
                    <div className="pickup-field" style={{ flex: 1 }}>
                        <label>Tipo de Recogida *</label>
                        <select name="pickupType" value={values.pickupType} onChange={handleChange} required>
                            <option value="oficina">Entrega en Oficina UPS (Drop Off)</option>
                            <option value="domicilio">Recogida a Domicilio (Pickup)</option>
                        </select>
                    </div>
                    <div className="pickup-field" style={{ flex: 1 }}>
                        <label>Fecha Preferida *</label>
                        <input
                            type="date"
                            name="date"
                            value={values.date}
                            onChange={handleChange}
                            required
                            min={getTomorrowDate()}
                            max={getMaxDate()}
                            className={errors.date ? "is-invalid" : ""}
                        />
                        {errors.date && <span className="pick-up-form-error-message">{errors.date}</span>}
                    </div>
                </div>

                <div className="pickup-form-row">
                    <div className="pickup-field pickup-field-full">
                        <label>Dirección Completa *</label>
                        {GOOGLE_API_KEY ? (
                            <AddressAutocomplete
                                value={values.address}
                                onChange={(value) => setValues(prev => ({ ...prev, address: value }))}
                                onAddressSelect={handleAddressSelect}
                                placeholder="1234 Main Street, Apt 5A"
                                inputName="address"
                                required
                                apiKey={GOOGLE_API_KEY}
                                inputClassName={errors.address ? "is-invalid" : ""}
                                maxLength={150}
                            />
                        ) : (
                            <input 
                                type="text" 
                                name="address" 
                                placeholder="1234 Main Street, Apt 5B" 
                                value={values.address} 
                                onChange={handleChange} 
                                maxLength={150}
                                required 
                                className={errors.address ? "is-invalid" : ""}
                            />
                        )}
                        {errors.address && <span className="pick-up-form-error-message">{errors.address}</span>}
                    </div>
                </div>

                <div className="pickup-form-row pickup-form-row-3">
                    <div className="pickup-field">
                        <label>Ciudad *</label>
                        <input type="text" name="city" placeholder="Miami" value={values.city} onChange={handleChange} maxLength={50} required className={errors.city ? "is-invalid" : ""} />
                        {errors.city && <span className="pick-up-form-error-message">{errors.city}</span>}
                    </div>
                    <div className="pickup-field">
                        <label>Estado *</label>
                        <input type="text" name="state" placeholder="FL" value={values.state} onChange={handleChange} maxLength={50} required className={errors.state ? "is-invalid" : ""} />
                        {errors.state && <span className="pick-up-form-error-message">{errors.state}</span>}
                    </div>
                    <div className="pickup-field">
                        <label>Código Postal *</label>
                        <input 
                            type="text" 
                            name="zipCode" 
                            placeholder="33101" 
                            value={values.zipCode} 
                            onChange={handleChange} 
                            onBlur={handleZipBlur}
                            maxLength={10}
                            required 
                            className={errors.zipCode || zipError ? "is-invalid" : ""}
                        />
                        {(zipError || errors.zipCode) && <span className="pick-up-form-error-message">{zipError || errors.zipCode}</span>}
                    </div>
                </div>

                <div className="pickup-form-row">
                    <div className="pickup-field pickup-field-full">
                        <label>Descripción del Contenido *</label>
                        <textarea name="notes" placeholder="ej. Ropa, repuestos, electrónicos..." value={values.notes} onChange={handleChange} maxLength={255} rows={2} required className={errors.notes ? "is-invalid" : ""} />
                        {errors.notes && <span className="pick-up-form-error-message">{errors.notes}</span>}
                    </div>
                </div>

                <button type="submit" className="pickup-submit-btn">
                    <FontAwesomeIcon icon={faTruckFast} /> Solicitar Recogida Ahora
                </button>
            </form>
        </div >
    );
};

export default PickupForm;
